import type { AppTrackingStatus } from 'capacitor-plugin-app-tracking-transparency';
import type { BootPhase } from 'src/modules/boot/bootPhase';

export interface PostRenderTailDeps {
  platform: string;
  requestAtt: () => Promise<AppTrackingStatus>;
  enableFacebookTrackingIfUnconfigured: (attAuthorized?: boolean) => Promise<void>;
  initAppsFlyerSdk: () => Promise<void>;
  startAppsFlyerSdk: () => void;
  setBootPhase: (phase: BootPhase) => void;
  // Live read of the boot watchdog's fired flag (bootWatchdog.ts), NOT a snapshot: the watchdog can
  // fire mid-tail — the iOS 17.4 ATT bug parks `requestAtt()` until the next `didBecomeActive` — so
  // this must be read after the awaits below, not captured before them.
  isWatchdogFired: () => boolean;
  armRenderProbe: (now: number) => void;
  now: () => number;
  setIsReady: (ready: boolean) => void;
  disarmBootWatchdog: () => void;
  reportError: (message: string, error?: unknown) => void;
}

export const runPostRenderTail = async (deps: PostRenderTailDeps): Promise<void> => {
  // Facebook auto-logging is deferred (disabled in Info.plist / AndroidManifest) so the install
  // event fires AFTER ATT consent with correct attribution.
  try {
    if (deps.platform === 'ios') {
      const attStatus = await deps.requestAtt();
      await deps.enableFacebookTrackingIfUnconfigured(attStatus === 'authorized');
    } else if (deps.platform === 'android') {
      await deps.enableFacebookTrackingIfUnconfigured();
    }
  } catch (error) {
    deps.reportError('Error enabling Facebook tracking during app init', error);
  }

  // Initialise (but do not start) AppsFlyer before render. initSDK with manualStart is synchronous
  // and network-free, and it attaches the deepLinkDelegate — so the module-scope UDL listener stays
  // valid and <AppsFlyer/> finds the SDK initialised, which is what guarantees configurePrivacy()
  // runs for children.
  try {
    await deps.initAppsFlyerSdk();
  } catch (error) {
    deps.reportError('Error initializing AppsFlyer during app init', error);
  }

  deps.setBootPhase('rendered');
  if (deps.isWatchdogFired()) {
    // This launch was already force-rendered by the watchdog above — its setIsReady(true) already
    // ran, and armRenderProbe's one-shot survival timer belongs to that earlier probe cycle, not
    // this late-completing tail.
  } else {
    // Arms the render-health marker right before the app is shown — after the blocking ATT prompt
    // and AppsFlyer have settled, which is what makes the B6 false positive impossible by
    // construction (see renderHealth.ts's armRenderProbe doc comment).
    deps.armRenderProbe(deps.now());
    deps.setIsReady(true);
  }
  // The tail completed — disarm either way.
  deps.disarmBootWatchdog();

  // Network-bound half, AFTER render.
  deps.startAppsFlyerSdk();
};
